import React, { memo } from 'react';
import classes from '../FlowNodes.module.scss';
import Typography from 'new/elements/Typography';
import { ReactComponent as FolderIcon } from 'new/components/icons/folder.svg';
import { useStore } from '@xyflow/react';
import { getRelativeNodesBounds } from 'js/components/pipelines/FlowNodes/utils';
import ProjectDetailsActions from 'new/components/pipelines/ProjectsList/ProjectDetailsActions';
import { useResourceDetailHandler } from 'new/components/ResourceDetailHandler/context';
import { DetailsIconButton } from 'new/elements/Button';
import { Stack } from 'new/elements/Stack';
import { isAdminAccess, isOwner } from 'js/lib/ACLUtils';

interface FlowItemProps {
  data: {
    selected: boolean;
    height: number;
    width: number;
    item: any;
    onOpenProjectDetail?: (project: any) => void;
    project?: any;
  };
}

const zoomSelector = (s) => s.transform[2];

export const ProjectNode = (props: FlowItemProps) => {
  const { data } = props;
  const { item, height, width } = data;

  const { onOpenProjectDetail } = useResourceDetailHandler();

  const zoom = useStore(zoomSelector);

  const { minWidth, minHeight } = useStore((store) => {
    const childNodes = Array.from(store.nodeLookup.values()).filter(
      (n) => n.parentId === 'project-' + item.id,
    );
    const rect = getRelativeNodesBounds(childNodes);

    return {
      minWidth: rect.x + rect.width,
      minHeight: rect.y + rect.height,
      hasChildNodes: childNodes.length > 0,
    };
  }, isEqual);

  const scale = 0.8 / zoom;
  const isAdmin = isOwner(item) || isAdminAccess(item);

  return (
    <div>
      <div
        style={{
          transformOrigin: 'bottom left',
          transform: `scale(${scale >= 1 ? scale : 1})`,
        }}
        className={classes.projectItemInfoContainer}
      >
        <FolderIcon />
        <Typography className={classes.projectItemName}>{item.name}</Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          {isAdmin ? (
            <Stack marginBottom={0.5}>
              <DetailsIconButton onClick={() => onOpenProjectDetail(item)} />
            </Stack>
          ) : null}
          <ProjectDetailsActions
            data={item}
            onDetailClicked={() => onOpenProjectDetail(item)}
            horizontalDots
            includeDetailsOption={false}
          />
        </Stack>
      </div>
      <div className={classes.nodesContainer} style={{ height, width, minHeight, minWidth }} />
    </div>
  );
};

type IsEqualCompareObj = {
  minWidth: number;
  minHeight: number;
  hasChildNodes: boolean;
};

function isEqual(prev: IsEqualCompareObj, next: IsEqualCompareObj): boolean {
  return (
    prev.minWidth === next.minWidth &&
    prev.minHeight === next.minHeight &&
    prev.hasChildNodes === next.hasChildNodes
  );
}

export default memo(ProjectNode);
